"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { COMPANY_INFO } from "@/data/companyInfo";
import Icon from "@/components/ui/Icon";

interface Message {
  sender: "user" | "ai";
  text: string;
}

const PRESET_ANSWERS: Record<string, string> = {
  "services": "We design, build, and scale high-performance web ecosystems, native mobile apps, and robust DevOps/AI pipelines. Check our Services page for details!",
  "consultation": "You can book a technical consultation directly via our Consultation page. We'll set up a 30-minute discovery call to align on your project.",
  "location": `We are headquartered at ${COMPANY_INFO.addressFormatted}, collaborating with clients worldwide.`,
  "audit": "Yes! We specialize in high-integrity software audits, performance optimization, and zero-trust security sweeps. Head over to our Contact page to initialize a project."
};

const SUGGESTIONS = [
  { text: "What services do you offer?", key: "services" },
  { text: "How do I book a consultation?", key: "consultation" },
  { text: "Where are you located?", key: "location" },
  { text: "Can you review our code/architecture?", key: "audit" }
];

export default function FloatingMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { sender: "ai", text: "Hello! I'm the Beyond Ambition AI Assistant. How can I help you today?" }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close floating menu & chat window when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsChatOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Monitor scroll height to show back-to-top option dynamically
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  const handleSendMessage = (textToSend: string) => {
    if (!textToSend.trim()) return;

    const newMessages: Message[] = [...messages, { sender: "user", text: textToSend }];
    setMessages(newMessages);
    setInputValue("");
    setIsTyping(true);

    const lower = textToSend.toLowerCase();
    let reply = "Thanks for your message! Our engineering team will review your query shortly. For urgent inquiries, email solutions@beyondambitionlimited.com or reach out on WhatsApp.";

    if (lower.includes("service") || lower.includes("offer") || lower.includes("build") || lower.includes("do")) {
      reply = PRESET_ANSWERS["services"];
    } else if (lower.includes("consult") || lower.includes("book") || lower.includes("meeting") || lower.includes("call")) {
      reply = PRESET_ANSWERS["consultation"];
    } else if (lower.includes("where") || lower.includes("location") || lower.includes("office") || lower.includes("address")) {
      reply = PRESET_ANSWERS["location"];
    } else if (lower.includes("audit") || lower.includes("security") || lower.includes("code") || lower.includes("review")) {
      reply = PRESET_ANSWERS["audit"];
    }

    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [...prev, { sender: "ai", text: reply }]);
    }, 1000);
  };

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsOpen(false);
  };

  const handleWhatsApp = () => {
    window.open(COMPANY_INFO.whatsappLink, "_blank");
    setIsOpen(false);
  };

  const floatingActions = [
    {
      id: "top",
      label: "Back to Top",
      show: showScrollTop,
      action: handleScrollTop,
      bgColor: "bg-white dark:bg-[#322B28] text-primary border border-outline-variant/20 dark:border-outline-variant/10",
      customIcon: <Icon name="arrow_upward" size={22} />
    },
    {
      id: "whatsapp",
      label: "WhatsApp Support",
      show: true,
      action: handleWhatsApp,
      bgColor: "bg-[#25D366] text-white",
      customIcon: (
        <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
          <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.436 2.5 1.171 3.479l-.762 2.784 2.852-.748c.954.582 2.068.919 3.262.919 3.178 0 5.767-2.587 5.768-5.766 0-3.18-2.589-5.766-5.768-5.766zm3.911 8.255c-.174.494-.852.897-1.175.932-.303.033-.699.043-1.144-.099-.291-.093-.664-.224-1.127-.417-1.921-.805-3.14-2.766-3.236-2.893-.095-.128-.777-1.034-.777-1.971 0-.936.48-1.399.664-1.597.16-.171.425-.25.68-.25.084 0 .161.004.23.007.207.01.314.015.452.348.174.417.597 1.458.648 1.563.051.104.084.225.015.362-.069.138-.104.225-.207.348-.104.117-.221.263-.314.348-.104.095-.213.197-.093.407.12.21.534.88 1.146 1.426.787.701 1.448.918 1.652 1.02.203.103.322.086.442-.051.12-.138.514-.597.649-.803.136-.206.273-.172.457-.103.185.069 1.174.553 1.378.656.204.103.339.155.389.24.051.086.051.493-.123.987z"/>
          <path fillRule="evenodd" d="M12.004 22c5.523 0 10-4.477 10-10S17.527 2 12.004 2 2.004 6.477 2.004 12s4.477 10 10 10zm0-1.5c-4.694 0-8.5-3.806-8.5-8.5s3.806-8.5 8.5-8.5 8.5 3.806 8.5 8.5-3.806 8.5-8.5 8.5z"/>
        </svg>
      )
    },
    {
      id: "ai",
      label: "AI Chat Assistant",
      show: true,
      action: () => {
        setIsChatOpen(true);
        setIsOpen(false);
      },
      bgColor: "bg-primary text-on-primary",
      customIcon: <Icon name="chat" size={22} />
    }
  ];

  return (
    <div ref={containerRef} className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end">
      {/* Expanded Menu Actions */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-end gap-3 mb-3 sm:mb-4"
          >
            {floatingActions.map(
              (act) =>
                act.show && (
                  <div key={act.id} className="flex items-center gap-2.5 sm:gap-3">
                    <span className="bg-surface-container dark:bg-surface-container-high text-on-surface text-[11px] sm:text-xs font-bold px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg shadow-sm border border-outline-variant/10 whitespace-nowrap">
                      {act.label}
                    </span>
                    <button
                      onClick={act.action}
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 transition-transform ${act.bgColor}`}
                      aria-label={act.label}
                    >
                      {act.customIcon}
                    </button>
                  </div>
                )
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Floating Action Button */}
      <button
        onClick={() => {
          if (isChatOpen) {
            setIsChatOpen(false);
          } else {
            setIsOpen(!isOpen);
          }
        }}
        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-xl flex items-center justify-center cursor-pointer transform hover:scale-105 active:scale-95 transition-all ${
          isOpen || isChatOpen 
            ? "bg-[#3C3530] dark:bg-[#3F3632] text-white" 
            : "bg-primary text-on-primary hover:shadow-[0_8px_20px_-4px_rgba(194,101,42,0.6)]"
        }`}
        aria-label="Toggle menu"
      >
        <motion.span
          animate={{ rotate: isOpen || isChatOpen ? 90 : 0 }}
          transition={{ type: "spring", damping: 15 }}
          className="flex items-center justify-center text-2xl sm:text-3xl"
        >
          <Icon name={isOpen || isChatOpen ? "close" : "chat"} size={22} className="sm:hidden" />
          <Icon name={isOpen || isChatOpen ? "close" : "chat"} size={26} className="hidden sm:inline-block" />
        </motion.span>
      </button>

      {/* AI Chat Dialog Drawer */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
            className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 w-[360px] max-w-[calc(100vw-32px)] h-[480px] max-h-[75vh] bg-white dark:bg-[#25201D] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-outline-variant/10"
          >
            {/* Header */}
            <div className="bg-primary px-4 py-4 flex justify-between items-center text-white">
              <div className="flex items-center gap-2.5">
                <Icon name="chat" size={22} />
                <div>
                  <h4 className="font-bold text-sm leading-tight">Beyond Ambition AI</h4>
                  <span className="text-[10px] text-white/80 flex items-center gap-1 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse"></span>
                    Online • Assistant
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setIsChatOpen(false)}
                className="text-white/80 hover:text-white cursor-pointer"
                aria-label="Close chat window"
              >
                <Icon name="close" size={22} />
              </button>
            </div>

            {/* Chat Messages Body */}
            <div className="flex-grow overflow-y-auto p-4 flex flex-col gap-3 bg-surface-container-lowest/40 dark:bg-surface-container-lowest/10">
              {messages.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`flex flex-col max-w-[80%] ${
                    msg.sender === "user" ? "self-end items-end" : "self-start items-start"
                  }`}
                >
                  <div 
                    className={`px-4 py-2.5 rounded-2xl text-[13px] leading-relaxed ${
                      msg.sender === "user" 
                        ? "bg-primary text-white rounded-tr-none" 
                        : "bg-surface-container dark:bg-surface-container-high text-on-surface rounded-tl-none border border-outline-variant/5"
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="self-start flex flex-col items-start max-w-[80%]">
                  <div className="bg-surface-container dark:bg-surface-container-high px-4 py-3 rounded-2xl rounded-tl-none border border-outline-variant/5 flex gap-1 items-center">
                    <span className="w-2 h-2 rounded-full bg-on-surface/40 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-on-surface/40 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-on-surface/40 animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions list */}
            {messages.length === 1 && !isTyping && (
              <div className="px-4 py-2 flex flex-col gap-1.5 border-t border-outline-variant/5 bg-white dark:bg-[#25201D]">
                <span className="text-[10px] font-bold text-secondary tracking-wider uppercase mb-0.5">Suggestions</span>
                <div className="flex flex-wrap gap-1.5 overflow-x-auto pb-1 max-h-[80px]">
                  {SUGGESTIONS.map((sug, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(sug.text)}
                      className="px-2.5 py-1 text-[11px] font-semibold text-primary bg-primary-light hover:bg-primary/10 rounded-full border border-primary/20 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      {sug.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Footer Input Area */}
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage(inputValue);
              }}
              className="p-3 border-t border-outline-variant/10 flex gap-2 items-center bg-white dark:bg-[#25201D]"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask something..."
                aria-label="Ask our AI assistant"
                className="flex-grow px-4 py-2 text-[13px] bg-surface-container-low dark:bg-surface-container-high text-on-surface rounded-xl border border-outline-variant/10 focus:outline-none focus:border-primary/50"
              />
              <button
                type="submit"
                disabled={!inputValue.trim()}
                className="w-9 h-9 rounded-xl bg-primary text-on-primary flex items-center justify-center cursor-pointer hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-default"
                aria-label="Send message"
              >
                <Icon name="send" size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
